import { useState, useEffect, useCallback } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import './Module1World.scss'
import '../../Animations.css'

import MediaService from '../../services/MediaService'
import { getVideo } from '../../s3Func/s3Download'

import Practice from './Module1Practice'
import Module1Config from './Module1Config'
import { getUserContent, sleep } from '../../utils'
import WorldContainer from '../../components/World/WorldContainer'

/**
 * Module1World is a base template for all worlds that follow it. In this case, this is for the
 * beach scene, but this code has been copied and pasted directly to worlds 1-5 with only slight
 * name changes to apply to each world. After world 5, there is an additional component of
 * Follow The Beat, which will have a comment writeup in Module6World.
 * @returns The HTML code for Module1World
 */
const Module1World = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()

	const { module1 } = useSelector((state) => state.module1, shallowEqual)
	const { login } = useSelector((state) => state.loginReducer, shallowEqual)
	const [slidesConfig, setSlidesConfig] = useState({})
	const [displayPracticeVideo, setDisplayPracticeVideo] = useState(false)
	const [displayEndingVideo, setDisplayEndingVideo] = useState(false)
	const [showYouAreAwesome, setShowYouAreAwesome] = useState(false)
	const [showRewardSelection, setShowRewardSelection] = useState(false)
	const [crabAnimation, setCrabAnimation] = useState(null)
	const [chestAnimationState, setChestAnimationState] = useState('')
	const [chestState, setChestState] = useState('')
	const [playChestShake, setPlayChestShake] = useState(false)
	const [playChestOpen, setPlayChestOpen] = useState(false)

	const WORLD_IMAGES = {
		progressBar0: '/Module1Images/ProgressBar_0.svg',
		progressBar1: '/Module1Images/ProgressBar_1-4.svg',
		progressBar2: '/Module1Images/ProgressBar_3-4.svg',
		progressBarCompleted: '/Module1Images/ProgressBar_Completed.svg',
		keyReward: '/Module1Images/Key_Full.png',

		gift: '/Images/Gift.png',

		closedChest: '/Module1Images/Chest_Closed.svg',
		openChest: '/Module1Images/Chest_Open.svg',

		animal: '/Module1Images/Crab2.gif',
		worldBackground: '/Module1Images/U1M1_Background_Landscape.png',
		logoutBtn: '/Module1Images/Logout.png',
		journeyImage: '/Module1Images/bluejourneymap.svg',
	}

	const WORLD_AUDIO = {
		chestShakingSound: '/Module1Videos/chest_shake.mp3',
		chestOpenSound: '/Module1Videos/chest_open_items_out.mp3',
	}

	const WORLD_REWARDS = {
		BeachChairBlue: '/Module1Images/Reward_ChairBlue.svg',
		BeachChairRed: '/Module1Images/Reward_ChairRed.svg',
		BeachwearSandles: '/Module1Images/Reward_Shoes.svg',
		BeachwearSnorkle: '/Module1Images/Reward_Googels.svg',
		FloatationBall: '/Module1Images/Reward_BeachBall.svg',
		SandBucket: '/Module1Images/Reward_Bucket.svg',
		CrabWearGlasses: '/Module1Images/Reward_Glasses.svg',
		CrabWearMustache: '/Module1Images/Reward_Mustache.svg',
		SandCastle: '/Module1Images/Reward_SandCastel.svg',
		SeashellsRed: '/Module1Images/Reward_StarFish.svg',
		OceanBoat: '/Module1Images/Reward_Ship.svg',
		SeashellsGreen: '/Module1Images/Reward_Shell.svg',
		SurfBoardBlue: '/Module1Images/Reward_SurfBoardBlue.svg',
		SurfBoardRed: '/Module1Images/Reward_SurfBoardRed.svg',
		UmbrellaGreen: '/Module1Images/Reward_UmbrellaGreen.svg',
		UmbrellaRed: '/Module1Images/Reward_UmbrellaRed.svg',
		FloatationInnerTube: '/Module1Images/Reward_Floaty.svg',
		OceanWhale: '/Module1Images/Reward_Whale.svg',
	}

	// Get AWS URL World Images
	Object.keys(WORLD_IMAGES).forEach((key) => {
		WORLD_IMAGES[key] = MediaService.getImage(WORLD_IMAGES[key], login)
	})

	// Get AWS URL Reward Images
	Object.keys(WORLD_REWARDS).forEach((key) => {
		WORLD_REWARDS[key] = MediaService.getImage(WORLD_REWARDS[key], login)
	})

	// Get AWS URL World Audio
	Object.keys(WORLD_AUDIO).forEach((key) => {
		WORLD_AUDIO[key] = MediaService.getVideo(WORLD_AUDIO[key], login)
	})

	const createLookingSlideShow = useCallback(async () => {
		const config = {
			navigate: '/eye-contact',
			json: '',
			song: '',
			recording: '',
		}

		const { learn } = Module1Config[`day${module1.currentday}`]

		if (learn) {
			if (module1.currentday <= 4) {
				config.song = getVideo(learn.song, login)
			} else {
				const userRecording = await getUserContent(
					learn.recording,
					module1.StudentID,
					login.influencerid,
					login
				  );
				  const song = await getUserContent(
					learn.song,
					module1.StudentID,
					login.influencerid,
					login
				  );
		  
				  const demoSong = await getUserContent(
					learn.demoSong,
					module1.StudentID,
					login.influencerid,
					login
				  );
				  fetch(userRecording).then((response) => {
					if (response.status === 200) {
					  config.recording = userRecording;
					  config.song = song;
					}
					else{
					  config.recording = null;
					  config.song = demoSong;
					}
		  
				  })
			}
		}

		config.json = learn.json

		setSlidesConfig(config)
	}, [])

	const getCrabAnimation = async () => {
		const animatedCrab = {}
		animatedCrab.img = await getVideo('appfiles/eye_contact/crab_awesome.gif', login)
		animatedCrab.sound = await getVideo('appfiles/eye_contact/eye_contact_day_finished.mp3', login)
		setCrabAnimation(animatedCrab)
	}

	const handleFinalAnimationEnd = async () => {
		await sleep(500)

		dispatch({
			type: `MODULE1_SET_DAY_UNLOCK${module1.currentday + 1}`,
			payload: true,
		})

		dispatch({
			type: 'MODULE1_SET_dayCompleted',
			payload: module1.currentday,
		})

		if (module1.currentday !== 9) {
			dispatch({
				type: 'MODULE1_SET_stepCompleted',
				payload: 'notstarted',
			})
			await sleep(100);
			navigate('/eye-contact/navigation')
		} else {
			setDisplayEndingVideo(true)
		}
	}

	useEffect(() => {
		createLookingSlideShow()
		getCrabAnimation()
	}, [module1.stepComplete, createLookingSlideShow])

	const rewardSelectionAnimation = async () => {
		await sleep(1000)
		setChestAnimationState('shakeChest')
		setPlayChestShake(true)
		await sleep(1400)
		setChestState(WORLD_IMAGES.openChest)
		setPlayChestOpen(true)
		await sleep(1000)
		setShowRewardSelection(true)
	}

	return (
		<>
			<WorldContainer
				name={"EyeContact"}
				module={module1}
				moduleName="MODULE1"
				moduleConfig={Module1Config}
				slidesConfig={slidesConfig}
				practice={
					<Practice
						setDisplayPractice={() => setDisplayPracticeVideo(false)}
						background={WORLD_IMAGES.worldBackground}
						stepComplete="MODULE1_SET_stepCompleted"
						module={module1}
					/>
				}
				route="/eye-contact"
				worldImages={WORLD_IMAGES}
				worldRewards={WORLD_REWARDS}
				showRewardSelection={showRewardSelection}
				uuid="eye_contact"
				onPracticeClick={() => setDisplayPracticeVideo(true)}
				displayPracticeVideo={displayPracticeVideo}
				onRewardKeyClick={rewardSelectionAnimation}
				onRewardSelected={() => (setShowYouAreAwesome(true), setShowRewardSelection(false))}
				displayEndingVideo={displayEndingVideo}
			>
				<div className="crab-character">
					<figure>
						<div className="crab-wearables">
							{module1.rewards.CrabwearGlasses && (
								<img
									className="crabWearGlasses"
									src={WORLD_REWARDS.CrabWearGlasses}
									alt="crab glasses"
								/>
							)}
							{module1.rewards.CrabWearMustash && (
								<img
									className="crabWearMustache"
									src={WORLD_REWARDS.CrabWearMustache}
									alt="crab mustache"
								/>
							)}
						</div>
						{!showYouAreAwesome && (
							<img src={WORLD_IMAGES.animal} alt="crab Icon" />
						)}

						{crabAnimation && (
							<img
								style={{
									opacity: showYouAreAwesome ? '1' : '0',
								}}
								src={crabAnimation.img}
								alt="crab animation Icon"
							/>
						)}

						{showYouAreAwesome && crabAnimation && (
							<audio autoPlay onEnded={handleFinalAnimationEnd}>
								<source src={crabAnimation.sound}></source>
							</audio>
						)}
					</figure>
				</div>
				<div className="closed-chest">
					<img
						src={chestState || WORLD_IMAGES.closedChest}
						id={chestAnimationState}
						alt="closed Chest Icon"
					/>

					{/* {playChestOpen && (
						<audio autoPlay>
							<source src={WORLD_AUDIO.chestOpenSound}></source>
						</audio>
					)} */}

					{playChestShake && (
						<audio autoPlay>
							<source src={WORLD_AUDIO.chestShakingSound}></source>
						</audio>
					)}
				</div>
			</WorldContainer>
		</>
	)
}
export default Module1World
